import { type ComponentProps, type ReactNode, Suspense, useEffect, useRef, useState, useTransition } from 'react';
import { Text } from 'react-aria-components';
import type { Selection } from 'react-stately';
import {
  disableClickhouseDependencies,
  enableFlagCleanupStatesFilter,
  enableNoMaintainerFlagFilter,
  isCodeReferencesEnabled,
  isDisableCodeReferencesWithOverrideEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { ensureCreationDateMarker } from '@gonfalon/flag-filters/ensureCreationDateMarker';
import { ensureFlagState } from '@gonfalon/flag-filters/ensureFlagState';
import { ensureFlagType } from '@gonfalon/flag-filters/ensureFlagType';
import { ensureSDKAvailability } from '@gonfalon/flag-filters/ensureSDKAvailability';
import type { CreationDate, FlagType, SDKAvailability, UIFlagFilter } from '@gonfalon/flag-filters/types';
import { MaintainerDialog } from '@gonfalon/maintainer/MaintainerDialog';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useApplication } from '@gonfalon/rest-api/hooks/useApplication';
import { useSuspenseApplication } from '@gonfalon/rest-api/hooks/useSuspenseApplication';
import { useSuspenseApplications } from '@gonfalon/rest-api/hooks/useSuspenseApplications';
import { useSuspenseTeam } from '@gonfalon/rest-api/hooks/useSuspenseTeam';
import { TextButton } from '@gonfalon/ui/Button';
import { DialogHeading } from '@gonfalon/ui/Dialog';
import { ListBoxItem } from '@gonfalon/ui/ListBox';
import type { ListBoxDialogProps } from '@gonfalon/ui/ListBoxDialog';
import { ListBoxDialog } from '@gonfalon/ui/ListBoxDialog';
import { Picker } from '@gonfalon/ui/Picker';
import { Stack } from '@gonfalon/ui/stack';
import { Tag, TagFallback, TagGroup, TagIcon } from '@gonfalon/ui/TagGroup';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogTrigger,
  ListBoxItem as LaunchpadListBoxItem,
  Popover,
  ProgressBar,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { EntityTagFilter } from '#components/EntityTagFilter/EntityTagFilter';

import styles from './FlagFiltersDialog.module.css';

export type HiddenFlagFilter = 'applicationEvaluated' | 'archivedState';

type Props = {
  filters: UIFlagFilter | undefined;
  onClear: (() => void) | undefined;
  onChange: (filters: UIFlagFilter) => void;
  hiddenFilters?: readonly HiddenFlagFilter[];
};

// UI-only sentinel value for the maintainer filters. The backend honors `maintainerId:none` /
// `maintainerTeamKey:none` only for session-authenticated requests with the
// enable-no-maintainer-flag-filter flag on (see internal/flags/filters/filter_parser.go).
const NO_MAINTAINER_FILTER_VALUE = 'none';

type MaintainerTagItem = { id: string; type: 'none' | 'member' | 'team' };

// Resolves the maintainer filter for display and selection in one place. The `none` sentinel means
// "no maintainer" (both id and team key null) and is never treated as a real member/team id — so it
// won't trigger a member/team lookup or render as a member/team tag. When the flag is off, a stale
// `none` in the URL resolves to no selection.
export function resolveMaintainerFilter(filters: UIFlagFilter, showNoMaintainerOption: boolean) {
  const { maintainerId, maintainerTeamKey } = filters;
  // Only the full "no maintainer" state (both fields null) shows as such. A lone sentinel is only
  // reachable via a stale/hand-crafted URL — treating it as "No maintainer" would imply both fields
  // are unset while the API only applies the one IS NULL clause that's present, so it resolves to no
  // selection instead.
  const hasNoMaintainer =
    showNoMaintainerOption &&
    maintainerId === NO_MAINTAINER_FILTER_VALUE &&
    maintainerTeamKey === NO_MAINTAINER_FILTER_VALUE;
  const effectiveMaintainerId = maintainerId === NO_MAINTAINER_FILTER_VALUE ? undefined : maintainerId;
  const effectiveMaintainerTeamKey = maintainerTeamKey === NO_MAINTAINER_FILTER_VALUE ? undefined : maintainerTeamKey;

  const tagItems: MaintainerTagItem[] = hasNoMaintainer
    ? [{ id: NO_MAINTAINER_FILTER_VALUE, type: 'none' }]
    : effectiveMaintainerId !== undefined
      ? [{ id: effectiveMaintainerId, type: 'member' }]
      : effectiveMaintainerTeamKey !== undefined
        ? [{ id: effectiveMaintainerTeamKey, type: 'team' }]
        : [];

  return {
    hasNoMaintainer,
    maintainerId: effectiveMaintainerId,
    maintainerTeamKey: effectiveMaintainerTeamKey,
    tagItems,
  };
}

export function FlagFiltersDialog({ filters = {}, onChange, onClear, hiddenFilters = [] }: Props) {
  const tagsFromFilters = filters.tags ?? [];

  // Optimistic tag state: updates immediately on click so the checkbox appears
  // checked before the URL navigation round-trip completes. Also prevents lost
  // clicks when the user selects multiple tags in quick succession (each click
  // builds on the optimistic state, not the stale URL-derived value).
  const [pendingTags, setPendingTags] = useState(tagsFromFilters);

  // Sync pendingTags when the URL actually updates (content-based comparison to
  // avoid re-syncing on every render caused by new array references from the parent).
  const filterTagsKey = tagsFromFilters.slice().sort().join('\0');
  useEffect(() => {
    setPendingTags(tagsFromFilters);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTagsKey]);

  const showNoMaintainerOption = enableNoMaintainerFlagFilter();
  const maintainer = resolveMaintainerFilter(filters, showNoMaintainerOption);

  const formRef = useRef<HTMLFormElement | null>(null);

  return (
    <Dialog className={styles.filters}>
      {({ close }) => (
        <>
          <header className={styles.header}>
            <DialogHeading onClose={close}>
              <Message id="flags.filters.heading" />
            </DialogHeading>
          </header>

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                onReset={reset}
                severity="medium"
                fallbackRender={({ error, resetErrorBoundary }) => {
                  let action: ReactNode | undefined;
                  if (isRESTAPIError(error) && error.status !== 403) {
                    action = (
                      <Button size="small" onPress={() => resetErrorBoundary()}>
                        {msg('flags.filters.tryAgain')}
                      </Button>
                    );
                  }

                  return (
                    <div>
                      <Alert variant="inline" status="error">
                        <Message id="flags.filters.loadError" />{' '}
                        {action !== undefined ? (
                          <ButtonGroup>{action}</ButtonGroup>
                        ) : (
                          <Message id="flags.filters.loadErrorNotified" />
                        )}
                      </Alert>
                    </div>
                  );
                }}
              >
                <div className={styles.controls}>
                  <form ref={formRef} className={styles.form}>
                    <div className={styles.project}>
                      <Stack gap="4">
                        <EntityTagFilter
                          tags={pendingTags}
                          onChange={(nextFilters) => {
                            setPendingTags(nextFilters.tags);
                            onChange({ ...filters, tags: nextFilters.tags });
                          }}
                        />

                        <TagGroup
                          label={msg('flags.maintainerFilter.label')}
                          onRemove={() =>
                            onChange({
                              ...filters,
                              maintainerId: undefined,
                              maintainerTeamKey: undefined,
                            })
                          }
                          items={maintainer.tagItems}
                          renderEmptyState={() => msg('flags.maintainerFilter.anyone')}
                          renderAddTrigger={() => (
                            <DialogTrigger>
                              <TextButton size="small">{msg('flags.maintainerFilter.choose')}</TextButton>
                              <Popover>
                                <Suspense
                                  fallback={
                                    <Box
                                      width="$320"
                                      height="$208"
                                      display="grid"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <ProgressBar
                                        aria-label={msg('flags.status.loading')}
                                        isIndeterminate
                                        size="small"
                                      />
                                    </Box>
                                  }
                                >
                                  <MaintainerDialog
                                    selectionMode="single"
                                    closeOnSelection
                                    maintainerId={maintainer.maintainerId}
                                    maintainerTeamKey={maintainer.maintainerTeamKey}
                                    noMaintainerOption={
                                      showNoMaintainerOption
                                        ? {
                                            isSelected: maintainer.hasNoMaintainer,
                                            // "No maintainer" = both fields null, so send both sentinels.
                                            onSelect: () =>
                                              onChange({
                                                ...filters,
                                                maintainerId: NO_MAINTAINER_FILTER_VALUE,
                                                maintainerTeamKey: NO_MAINTAINER_FILTER_VALUE,
                                              }),
                                          }
                                        : undefined
                                    }
                                    onMemberSelectionChange={(key) =>
                                      onChange({
                                        ...filters,
                                        maintainerTeamKey: undefined,
                                        maintainerId: key !== undefined ? String(key) : undefined,
                                      })
                                    }
                                    onTeamSelectionChange={(key) =>
                                      onChange({
                                        ...filters,
                                        maintainerId: undefined,
                                        maintainerTeamKey: key !== undefined ? String(key) : undefined,
                                      })
                                    }
                                  />
                                </Suspense>
                              </Popover>
                            </DialogTrigger>
                          )}
                        >
                          {({ id, type }) => (
                            <Suspense
                              fallback={
                                <TagFallback>
                                  <TagFallback.Visual />
                                  <TagFallback.Text />
                                </TagFallback>
                              }
                            >
                              {type === 'member' ? (
                                <MemberMaintainerTag maintainerId={id} />
                              ) : type === 'team' ? (
                                <TeamMaintainerTag maintainerTeamKey={id} />
                              ) : type === 'none' ? (
                                <Tag id={id} textValue={msg('flags.maintainerFilter.noMaintainer')}>
                                  {msg('flags.maintainerFilter.noMaintainer')}
                                </Tag>
                              ) : null}
                            </Suspense>
                          )}
                        </TagGroup>

                        <FlagLifecycleTagGroup
                          value={filters.state}
                          hideArchived={hiddenFilters.includes('archivedState')}
                          onChange={(key) =>
                            onChange({ ...filters, state: key !== '' ? ensureFlagState(key) : undefined })
                          }
                        />

                        <FlagTypeTagGroup
                          value={filters.type}
                          onChange={(key) =>
                            onChange({ ...filters, type: key !== undefined ? ensureFlagType(key) : undefined })
                          }
                        />

                        {!hiddenFilters.includes('applicationEvaluated') && (
                          <TagGroup
                            label={msg('flags.evaluatedApplicationFilter.label')}
                            onRemove={() =>
                              onChange({
                                ...filters,
                                applicationEvaluated: undefined,
                              })
                            }
                            items={filters.applicationEvaluated ? [{ id: filters.applicationEvaluated }] : []}
                            renderEmptyState={() => msg('flags.evaluatedApplicationFilter.anyApplication')}
                            renderAddTrigger={() => (
                              <DialogTrigger>
                                <TextButton size="small">{msg('flags.evaluatedApplicationFilter.choose')}</TextButton>
                                <Popover>
                                  <Suspense
                                    fallback={
                                      <Box
                                        width="$192"
                                        height="$208"
                                        display="grid"
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        <ProgressBar
                                          aria-label={msg('flags.status.loading')}
                                          isIndeterminate
                                          size="small"
                                        />
                                      </Box>
                                    }
                                  >
                                    <ApplicationDialog
                                      title={msg('flags.evaluatedApplicationFilter.dialogTitle')}
                                      applicationKey={filters.applicationEvaluated}
                                      onSelectionChange={(selection) => {
                                        // only single selection is allowed
                                        if (selection === 'all') {
                                          return;
                                        }

                                        onChange({
                                          ...filters,
                                          applicationEvaluated:
                                            selection.size > 0 ? String(Array.from(selection)[0]) : undefined,
                                        });
                                      }}
                                    />
                                  </Suspense>
                                </Popover>
                              </DialogTrigger>
                            )}
                          >
                            {({ id }) => (
                              <Suspense
                                fallback={
                                  <TagFallback>
                                    <TagFallback.Visual />
                                    <TagFallback.Text />
                                  </TagFallback>
                                }
                              >
                                <ApplicationTag applicationKey={id} />
                              </Suspense>
                            )}
                          </TagGroup>
                        )}

                        <SDKAvailabilityPicker
                          availability={filters.sdkAvailability}
                          onSelectionChange={(key) => {
                            onChange({
                              ...filters,
                              sdkAvailability: key !== '' ? ensureSDKAvailability(key) : undefined,
                            });
                          }}
                        />

                        <CreationDatePicker
                          value={filters.created}
                          onSelectionChange={(value) =>
                            onChange({
                              ...filters,
                              created: value !== '' ? ensureCreationDateMarker(value) : undefined,
                            })
                          }
                        />
                      </Stack>
                    </div>
                  </form>
                </div>

                {onClear && (
                  <div className={styles.footer}>
                    <Button
                      size="small"
                      onPress={() => {
                        formRef.current?.reset();
                        onClear();
                      }}
                    >
                      {msg('flags.filters.clear')}
                    </Button>
                  </div>
                )}
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </>
      )}
    </Dialog>
  );
}

function ApplicationTag({ applicationKey }: { applicationKey: string }) {
  const { data: app } = useSuspenseApplication({ applicationKey });

  return (
    <Tag aria-label={app.name} id={app.key} textValue={app.name}>
      <TextTruncator>{app.name}</TextTruncator>
    </Tag>
  );
}

function TeamMaintainerTag({ maintainerTeamKey }: { maintainerTeamKey: string }) {
  const { data: team } = useSuspenseTeam({ teamKey: maintainerTeamKey });

  return (
    <Tag aria-label={team.name} id={maintainerTeamKey} textValue={maintainerTeamKey}>
      <TagIcon>
        <Icon name="group" size="small" />
      </TagIcon>{' '}
      <TextTruncator>{team.name}</TextTruncator>
    </Tag>
  );
}

type ApplicationDialogProps = Omit<
  ListBoxDialogProps<{ id: string; name: string; description: string | undefined }>,
  'isBusy' | 'onFilterChange' | 'children'
> & {
  applicationKey?: string;
};

function ApplicationDialog({ applicationKey, ...props }: ApplicationDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const { data } = useSuspenseApplications({
    params: {
      limit: 10,
      sort: 'name',
      filter:
        filterText !== ''
          ? {
              key: filterText,
              name: filterText,
            }
          : undefined,
    },
  });

  // The coercion here is a bit weird: the queries won't run unless the values exist, but TypeScript can't infer that
  // from the enabled option on the queries.
  const selectedApp = useApplication(
    { applicationKey: applicationKey ?? '' },
    { enabled: applicationKey !== undefined },
  );

  const apps = data.items ?? [];

  const items: Array<{ id: string; name: string; description: string | undefined }> = [];
  for (const app of apps) {
    if (app.key === applicationKey && (filterText === undefined || filterText === '')) {
      continue;
    }

    items.push({ id: app.key, name: app.name, description: app.description });
  }

  if (filterText === undefined || filterText === '') {
    if (selectedApp.status === 'success') {
      items.unshift({
        id: selectedApp.data.key,
        name: selectedApp.data.name,
        description: selectedApp.data.description,
      });
    }
  }

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog
      {...props}
      aria-label={msg('flags.evaluatedApplicationFilter.listLabel')}
      items={items}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
      selectionMode="single"
      defaultSelectedKeys={applicationKey !== undefined ? [applicationKey] : []}
      renderEmptyState={() => msg('flags.evaluatedApplicationFilter.noItemsFound')}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.id}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

export type FlagLifecycleTagGroupProps = {
  value: string | undefined;
  onChange: (value: string) => void;
  hideArchived?: boolean;
};

export function FlagLifecycleTagGroup({ value, onChange, hideArchived = false }: FlagLifecycleTagGroupProps) {
  const defaultValue = value ?? 'live';

  return (
    <TagGroup
      label={msg('flags.lifecycleFilter.label')}
      selectionMode="single"
      defaultSelectedKeys={new Set([defaultValue])}
      onSelectionChange={(keys: Selection) => {
        let candidate: string;
        if (typeof keys === 'string') {
          candidate = keys;
        } else if (keys instanceof Set) {
          candidate = Array.from(keys)[0] as string;
        } else {
          candidate = 'live';
        }
        onChange(candidate);
      }}
    >
      <Tag id="live">{msg('flags.lifecycleFilter.live')}</Tag>
      {enableFlagCleanupStatesFilter() && !disableClickhouseDependencies() && (
        <>
          {!isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled() && (
            <Tag id="readyForCodeRemoval">{msg('flags.lifecycleFilter.readyForCodeRemoval')}</Tag>
          )}
          <Tag id="readyToArchive">{msg('flags.lifecycleFilter.readyToArchive')}</Tag>
        </>
      )}
      {!hideArchived && <Tag id="archived">{msg('flags.lifecycleFilter.archived')}</Tag>}
      <Tag id="deprecated">{msg('flags.lifecycleFilter.deprecated')}</Tag>
    </TagGroup>
  );
}

type FlagTypeTagGroupProps = {
  value: FlagType | undefined;
  onChange: (value: FlagType | undefined) => void;
};

export function FlagTypeTagGroup({ value, onChange }: FlagTypeTagGroupProps) {
  const defaultValue = value === undefined ? 'any' : value;

  return (
    <TagGroup
      label={msg('flags.typeFilter.label')}
      selectionMode="single"
      defaultSelectedKeys={new Set([defaultValue])}
      onSelectionChange={(keys: Selection) => {
        let theValue: string;
        if (typeof keys === 'string') {
          theValue = keys;
        } else {
          theValue = String(Array.from(keys)[0]);
        }

        // Only accept valid candidates: "temporary", "permanent", or "any"
        if (theValue === 'temporary' || theValue === 'permanent' || theValue === 'any') {
          // Map "any" to undefined (which is valid for FlagType), otherwise use the candidate directly.
          const flagValue: 'permanent' | 'temporary' | undefined = theValue === 'any' ? undefined : theValue;
          onChange(flagValue);
        } else {
          onChange(undefined);
        }
      }}
    >
      <Tag id="any">{msg('flags.typeFilter.any')}</Tag>
      <Tag id="temporary">{msg('flags.typeFilter.temporary')}</Tag>
      <Tag id="permanent">{msg('flags.typeFilter.permanent')}</Tag>
    </TagGroup>
  );
}

function SDKAvailabilityPicker({
  availability,
  onSelectionChange,
}: {
  availability: SDKAvailability | undefined;
  onSelectionChange: ComponentProps<typeof Picker>['onSelectionChange'];
}) {
  return (
    <Picker
      label={msg('flags.sdkAvailabilityFilter.label')}
      defaultSelectedKey={availability ?? ''}
      onSelectionChange={onSelectionChange}
    >
      <LaunchpadListBoxItem id="" textValue="any">
        <Text slot="label">
          <Message id="flags.sdkAvailabilityFilter.allSdks" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="anyClient">
        <Text slot="label">
          <Message id="flags.sdkAvailabilityFilter.allClientSideSdks" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="client">
        <Text slot="label">
          <Message id="flags.sdkAvailabilityFilter.clientSideId" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="mobile">
        <Text slot="label">
          <Message id="flags.sdkAvailabilityFilter.mobileKey" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="server">
        <Text slot="label">
          <Message id="flags.sdkAvailabilityFilter.noClientSideSdks" />
        </Text>
      </LaunchpadListBoxItem>
    </Picker>
  );
}

function CreationDatePicker({
  value,
  onSelectionChange,
}: {
  value: CreationDate | undefined;
  onSelectionChange: ComponentProps<typeof Picker>['onSelectionChange'];
}) {
  return (
    <Picker
      label={msg('flags.creationDateFilter.label')}
      defaultSelectedKey={value ?? ''}
      onSelectionChange={onSelectionChange}
    >
      <LaunchpadListBoxItem id="" textValue="any">
        <Text slot="label">
          <Message id="flags.creationDateFilter.anytime" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="1-week-ago" textValue={msg('flags.creationDateFilter.oneWeekAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanOneWeekAgo" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="1-month-ago" textValue={msg('flags.creationDateFilter.oneMonthAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanOneMonthAgo" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="2-months-ago" textValue={msg('flags.creationDateFilter.twoMonthsAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanTwoMonthsAgo" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="3-months-ago" textValue={msg('flags.creationDateFilter.threeMonthsAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanThreeMonthsAgo" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="6-months-ago" textValue={msg('flags.creationDateFilter.sixMonthsAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanSixMonthsAgo" />
        </Text>
      </LaunchpadListBoxItem>
      <LaunchpadListBoxItem id="1-year-ago" textValue={msg('flags.creationDateFilter.oneYearAgo')}>
        <Text slot="label">
          <Message id="flags.creationDateFilter.moreThanOneYearAgo" />
        </Text>
      </LaunchpadListBoxItem>
    </Picker>
  );
}
